import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import "../styles/Navbar.css";
import { FiSearch, FiMenu, FiX } from "react-icons/fi";
import logo from "../Utils/Logos/logo.png";
import AuthModal from "../Auth/AuthModal";
import { useAuth } from "../Auth/AuthContext";
import { useTheme } from "../ThemeContext";
import { FaSun, FaMoon, FaUserCircle, FaSignOutAlt, FaTicketAlt, FaCog } from "react-icons/fa"; // ⬅️ update imports
import { FaHeart } from "react-icons/fa6";

const Navbar = ({ activeTab, setActiveTab, onAuthClick }) => {
  const [showSearch, setShowSearch] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const [isLogin, setIsLogin] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isScrolled, setIsScrolled] = useState(false);
  const [isNavExpanded, setIsNavExpanded] = useState(false);

  const { user, isAuthenticated, handleLogout } = useAuth();
  const { theme, toggleTheme } = useTheme();

  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const searchRef = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();

  const handleNavigate = (path) => {
    navigate(path);
    setIsNavExpanded(false); // Close mobile menu on navigation
  };

  // Handle logout with toast notification
  const handleLogoutWithToast = () => {
    handleLogout();
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showDropdown]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Prevent closing if the user is clicking the actual toggle button
      if (event.target.closest(".search-toggle-btn")) return;

      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSearch(false);
      }
    };

    if (showSearch) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showSearch]);

  const handleShowModal = (formType) => {
    setShowAuth(true);
    setIsLogin(formType === "login");
  };

  const handleCloseModal = () => setShowAuth(false);

  const toggleSearch = () => setShowSearch((prev) => !prev);

  const getInitials = (name) => {
    if (!name) return "U";
    const parts = name.trim().split(" ");
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (
      parts[0].charAt(0) + parts[parts.length - 1].charAt(0)
    ).toUpperCase();
  };

  // ✅ Handle search submit
  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim() === "") return;
    navigate(`/events?q=${encodeURIComponent(searchQuery.trim())}`);
    setShowSearch(false);
    setSearchQuery("");
  };

  // const avatarColors = [
  //   "#FF6B6B", // coral red
  //   "#F06595", // pink
  //   "#845EF7", // purple
  //   "#5C7CFA", // indigo
  //   "#339AF0", // blue
  //   "#22B8CF", // cyan
  //   "#20C997", // teal
  //   "#51CF66", // green
  //   "#FCC419", // yellow (dark enough for white)
  //   "#FF922B", // orange
  // ];

  // // Pick a random color from list
  // const getRandomColor = () => {
  //   return avatarColors[Math.floor(Math.random() * avatarColors.length)];
  // };

  // const [avatarColor, setAvatarColor] = useState(getRandomColor());

  // // Change color every 5 seconds
  // useEffect(() => {
  //   const interval = setInterval(() => {
  //     setAvatarColor(getRandomColor());
  //   }, 5000);

  //   return () => clearInterval(interval); // cleanup interval
  // });

  return (
    <div className={`main-nav ${isScrolled ? "scrolled" : ""}`}>
      <nav className="navbar navbar-expand-lg py-3">
        <div className="container">
          <div
            className="navbar-brand d-flex align-items-center"
            onClick={() => handleNavigate("/")}
          >
            <img src={logo} className="logo-icon" alt="Logo" />
            <span className="logo-text ms-2">Tixoza</span>
          </div>

          <button
            className="navbar-toggler"
            type="button"
            data-bs-toggle="collapse"
            data-bs-target="#navbarNav"
            aria-controls="navbarNav"
            aria-expanded={isNavExpanded}
            aria-label="Toggle navigation"
            onClick={() => setIsNavExpanded(!isNavExpanded)}
          >
            {isNavExpanded ? <FiX size={26} color="var(--text-color)" /> : <FiMenu size={26} color="var(--text-color)" />}
          </button>

          <div className={`collapse navbar-collapse ${isNavExpanded ? 'show' : ''}`} id="navbarNav">
            <ul className="navbar-nav mx-auto mb-2 mb-lg-0">
              <li className="nav-item">
                <span
                  className={`nav-link ${location.pathname === "/" ? "active" : ""
                    }`}
                  onClick={() => handleNavigate("/")}
                >
                  Home
                </span>
              </li>

              <li className="nav-item">
                <span
                  className={`nav-link ${location.pathname.startsWith("/events") ? "active" : ""
                    }`}
                  onClick={() => handleNavigate("/events")}
                >
                  Events
                </span>
              </li>

              {isAuthenticated && (
                <li className="nav-item">
                  <span
                    className={`nav-link ${location.pathname === "/wishlist" ? "active" : ""
                      }`}
                    onClick={() => handleNavigate("/wishlist")}
                  >
                    Wishlist
                  </span>
                </li>
              )}
              {isAuthenticated && user?.role === "organizer" ? (
                <li className="nav-item">
                  <span
                    className={`nav-link ${location.pathname === "/dashboard" ? "active" : ""
                      }`}
                    onClick={() => handleNavigate("/dashboard")}
                  >
                    Organizer Dashboard
                  </span>
                </li>
              ) : !(isAuthenticated && user?.role === "user") && (
                <li className="nav-item">
                  <span
                    className={`nav-link ${location.pathname === "/for-organizers" ? "active" : ""
                      }`}
                    onClick={() => handleNavigate("/for-organizers")}
                  >
                    For Organizers
                  </span>
                </li>
              )}
            </ul>

            {/* ✅ Conditional render for guest vs logged-in user */}
            <div className="d-flex align-items-center justify-content-center gap-3 mt-4 mt-lg-0 pb-3 pb-lg-0 nav-actions-wrapper">
              <div
                className={`theme-toggle ${theme}`}
                onClick={toggleTheme}
                title={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
              >
                <div className="toggle-track">
                  <div className="toggle-thumb">
                    <div className="thumb-shape"></div>
                  </div>
                  <div className="sky-elements">
                    <div className="sky-elem star1"></div>
                    <div className="sky-elem star2"></div>
                    <div className="sky-elem star3"></div>
                  </div>
                </div>
              </div>

              <span
                className={`icon-btn search-toggle-btn ${showSearch ? "active" : ""}`}
                onClick={toggleSearch}
              >
                <FiSearch className="fs-5 search-icon" />
              </span>

              {!isAuthenticated ? (
                <>
                  {/* Guest view */}
                  <button
                    className="nav-auth-btn nav-btn-outline px-4"
                    onClick={() =>
                      onAuthClick ? onAuthClick() : handleShowModal("login")
                    }
                  >
                    Login
                  </button>
                  <button
                    onClick={() => handleShowModal("signup")}
                    className="nav-auth-btn nav-btn-solid px-4"
                  >
                    Signup
                  </button>
                </>
              ) : (
                <>
                  {/* Logged-in view */}
                  {/* <SlBell className="fs-5 text-secondary bell-icon" /> */}
                  <div className="profile-wrapper" ref={dropdownRef}>
                    <div
                      className="profile-avatar"
                      // style={{ backgroundColor: avatarColor }}
                      onClick={() => setShowDropdown((prev) => !prev)}
                    >
                      {getInitials(user?.name)}
                    </div>

                    {showDropdown && (
                      <div className="profile-dropdown">
                        <div className="dropdown-header">
                          <strong>{user?.name || "Alex Johnson"}</strong>
                          <p>{user?.email || "alex.johnson@example.com"}</p>
                        </div>
                        <div className="dropdown-options">
                          <button onClick={() => handleNavigate("/my-profile")}>
                            <FaCog /> Profile & Settings
                          </button>
                          <button onClick={() => handleNavigate("/my-tickets")}>
                            <FaTicketAlt /> My Tickets
                          </button>
                          {user?.role === "organizer" && (
                            <button
                              onClick={() =>
                                handleNavigate("/dashboard")
                              }
                            >
                              <FaUserCircle /> Organizer Dashboard
                            </button>
                          )}
                          <button onClick={() => handleNavigate("/wishlist")}>
                            <FaHeart /> Wishlist
                          </button>

                          {/* <button
                            onClick={() => navigate("/dashboard")}
                          >
                            <FaUserCircle /> Organizer Dashboard
                          </button> */}
                        </div>
                        <hr />
                        <button className="logout-btn" onClick={handleLogoutWithToast}>
                          <FaSignOutAlt /> Log out
                        </button>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div >
      </nav >

      {/* ✅ Search Bar Section */}
      < div
        className={`search-bar-container ${showSearch ? "show" : ""}`}
        ref={searchRef}
      >
        <div className="container py-3">
          <form onSubmit={handleSearch}>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search events, organizers, city, or category..."
              className="form-control search-input"
              autoFocus
            />
          </form>
        </div>
      </div >

      <AuthModal
        show={showAuth}
        handleClose={handleCloseModal}
        isLogin={isLogin}
        setIsLogin={setIsLogin}
      />
    </div >
  );
};

export default Navbar;
